var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Log, LogCategory, Operation } from '@augloop/logging';
import deepEqual from 'fast-deep-equal';
import { DataListener } from './data-listener';
import { ConfigPatchOperationHelper } from './patch-operation';
export function isValidateCorrectUsageOfGetValue() {
    var _a;
    return typeof globalThis.process !== 'undefined' && ((_a = globalThis.process.env) === null || _a === void 0 ? void 0 : _a.VALIDATE_CORRECT_USAGE_OF_GET_VALUE) === 'true';
}
/**
 * Factory for settings and manager of the current composite configuration
 */
export class Setting extends DataListener {
    constructor(name) {
        super();
        this.name = name;
    }
    static initEcsSettingsProvider(ecsSettingsProvider) {
        Log.info(0x1e28f08a /* tag_4kpck */, LogCategory.CoreDefault, 'Initializing ECS settings provider.');
        Setting.ecsSettingsProvider = ecsSettingsProvider;
    }
    static enableEcsPatchConfig(ecsPatchConfigEnabled) {
        Setting.ecsPatchConfigEnabled = ecsPatchConfigEnabled;
    }
    /**
     * Retrives or creates a setting with the given name
     * @param name name of the setting
     */
    static getInstance(name) {
        let setting = this.allSettings.get(name);
        if (!setting) {
            setting = new Setting(name);
            const result = Setting.tryGetConfigProperty(Setting.currentConfig, name);
            if (result.success) {
                setting.updateValue(result.value);
            }
            this.allSettings.set(name, setting);
        }
        return setting;
    }
    /**
     * Retrives or creates a setting with the given pattern
     * @param pattern pattern for the setting names included in the setting
     */
    static getPatternInstance(pattern) {
        let settingRegexPair = this.allPatternSettings.get(pattern);
        if (!settingRegexPair) {
            // eslint-disable-next-line security/detect-non-literal-regexp
            settingRegexPair = { regex: new RegExp(pattern), setting: new Setting(pattern) };
            const value = Setting.tryGetConfigPropertyByPattern(Setting.currentConfig, settingRegexPair.regex);
            settingRegexPair.setting.updateValue(value);
            this.allPatternSettings.set(pattern, settingRegexPair);
        }
        return settingRegexPair.setting;
    }
    /**
     * Applies a new composite configuration to the current set of settings
     * @param config new config to be applied
     */
    static setNewConfig(config) {
        Setting.currentConfig = config;
        Log.info(0x2224d04e /* tag_8jnbo */, LogCategory.CoreDefault, `New config: ${JSON.stringify(config)}`);
        Setting.allSettings.forEach((setting, name) => {
            const value = Setting.tryGetConfigProperty(Setting.currentConfig, name).value;
            if (setting.updateValue(value)) {
                Log.info(0x2224d04f /* tag_8jnbp */, LogCategory.CoreDefault, `Setting new value for ${name}: ${value instanceof Object ? JSON.stringify(value) : value}`);
                setting.notifyListeners(value);
            }
        });
        Setting.allPatternSettings.forEach((settingRegexPair, pattern) => {
            const value = Setting.tryGetConfigPropertyByPattern(config, settingRegexPair.regex);
            if (settingRegexPair.setting.updateValue(value)) {
                Log.info(0x1e4e1186 /* tag_4t7gg */, LogCategory.CoreDefault, `Setting new value for pattern ${pattern}: ${JSON.stringify(value)}`);
                settingRegexPair.setting.notifyListeners(value);
            }
        });
    }
    static tryGetConfigProperty(config, name) {
        if (config && config.hasOwnProperty(name)) {
            return { success: true, value: Setting.selectApplicableValue(config[name]) };
        }
        return { success: false, value: undefined };
    }
    static tryGetConfigPropertyByPattern(config, pattern) {
        const result = [];
        if (config) {
            const settingNames = Object.getOwnPropertyNames(config).filter(settingName => pattern.test(settingName));
            for (const settingName of settingNames) {
                result.push({
                    name: settingName,
                    value: Setting.selectApplicableValue(config[settingName])
                });
            }
        }
        return result;
    }
    /**
     * Sets up a predicate to filter settings from the composite configuration
     * @param filter filtering predicate
     */
    static setGlobalFilter(filter) {
        Setting.globalFilter = filter;
    }
    static setLocalConfig(config) {
        Setting.localConfig = config;
        Log.info(0x1e29c4d1 /* tag_4k2tr */, LogCategory.CoreDefault, `Setting localConfig field in Setting class: ${JSON.stringify(Setting.localConfig)}`);
    }
    /**
     * Clears all settings
     */
    static clear() {
        Setting.currentConfig = undefined;
        Setting.localConfig = undefined;
        Setting.allSettings.clear();
        Setting.allPatternSettings.clear();
        Setting.ecsSettingsProvider = undefined;
    }
    static selectApplicableValue(values) {
        let value;
        if (values) {
            const filteredResult = values.find((val) => {
                return !Setting.globalFilter || Setting.globalFilter(val);
            });
            if (filteredResult) {
                value = filteredResult.value;
            }
        }
        return value;
    }
    /**
     * Returns setting name
     */
    getName() {
        return this.name;
    }
    /**
   * Returns setting value
   */
    getValue() {
        if (isValidateCorrectUsageOfGetValue() && this.listeners.size === 0) {
            const error = new Error(`SettingInstance or ChangeGate is being used incorrectly. Make sure .getValue() or ChangeGate is called during runtime or there is a listener set up on the setting. For more info visit: ${Setting.troubleshootUrl}`);
            error.name = 'Incorrect usage of getValue()';
            throw error;
        }
        return this.value;
    }
    getValueAdvancedAsync(input) {
        return __awaiter(this, void 0, void 0, function* () {
            const logOp = new Operation({
                operationName: 'ECSGetValueAsync',
            }, {
                // Metric Options
                metricDuration: true,
            }).start();
            let userDebugMessage;
            try {
                const getSettingValueResponse = yield Setting.ecsSettingsProvider.getSettingValue(this.name, input);
                logOp.dimension0 = getSettingValueResponse.userAuthenticated ? 'Authenticated' : 'Anonymous';
                logOp.success = true;
                let res = getSettingValueResponse.value;
                if (!res) {
                    res = Setting.tryGetConfigProperty(Setting.localConfig, this.name).value;
                    if (res && getSettingValueResponse.patchValue) {
                        // apply patch just on the setting
                        res = Setting.applyPatchOperationOnSetting(this.name, res, getSettingValueResponse.patchValue);
                    }
                }
                userDebugMessage = `Setting value for ${this.name} is ${JSON.stringify(res)}.`;
                return res;
            }
            catch (_a) {
                logOp.success = false;
                const resCatch = Setting.tryGetConfigProperty(Setting.localConfig, this.name).value;
                userDebugMessage = `Setting value for ${this.name} is ${JSON.stringify(resCatch)}.`;
                return resCatch;
            }
            finally {
                Log.info(0x1e29c4cf /* tag_4k2tp */, LogCategory.CoreDefault, logOp.stop());
                Log.info(0x1e263888 /* tag_4j98i */, LogCategory.CoreDefault, userDebugMessage);
            }
        });
    }
    updateValue(value) {
        if (!deepEqual(this.value, value)) {
            Log.info(0x2224d050 /* tag_8jnbq */, LogCategory.CoreDefault, `Received new property value for ${this.getName()}:`, value);
            this.value = value;
            return true;
        }
        return false;
    }
    static applyPatchOperationsOnConfig(config) {
        try {
            if (!Setting.ecsPatchConfigEnabled) {
                return config;
            }
            const patchOperations = this.tryGetConfigProperty(config, 'patchOperations').value;
            if (!patchOperations || patchOperations.length === 0) {
                return config;
            }
            const configOperations = [];
            for (const [settingName, patchValue] of Object.entries(patchOperations)) {
                try {
                    configOperations.push(ConfigPatchOperationHelper.parseECSOperation(settingName, patchValue));
                }
                catch (error) {
                    Log.error(0x1e24e70a /* tag_4jo2k */, LogCategory.CoreDefault, `Exception thrown while parsing ECS PATCH operation for setting: ${settingName}. Error: ${error}`);
                    continue;
                }
            }
            const updatedSettingsForLogging = new Map();
            for (const operation of configOperations) {
                if (config === null || config === void 0 ? void 0 : config.hasOwnProperty(operation.settingName)) {
                    // deep copy the config value to avoid modifying the original object
                    const resultValues = JSON.parse(JSON.stringify(config[operation.settingName]));
                    const settingValue = Setting.selectApplicableValue(resultValues);
                    const success = ConfigPatchOperationHelper.applyECSPatchOperation(operation, settingValue);
                    if (success) {
                        updatedSettingsForLogging.set(operation.settingName, settingValue);
                        config[operation.settingName] = resultValues;
                    }
                }
            }
            // Log the new updated settings after PATCH has been applied
            if (updatedSettingsForLogging.size > 0) {
                Log.info(0x1e25b7a2 /* tag_4j148 */, LogCategory.CoreDefault, `Resulting settings after PATCHes were applied: ${JSON.stringify(Object.fromEntries(updatedSettingsForLogging))}`);
            }
            return config;
        }
        catch (error) {
            Log.error(0x1e25b7a1 /* tag_4j147 */, LogCategory.CoreDefault, `Exception thrown while applying ECS PATCH operations. Error: ${error}`);
            return config;
        }
    }
    static applyPatchOperationOnSetting(settingName, settingValue, patchSettingValue) {
        try {
            if (!Setting.ecsPatchConfigEnabled) {
                return settingValue;
            }
            // create a deep copy of original setting value to avoid modifying the original object
            const resultSettingValue = JSON.parse(JSON.stringify(settingValue));
            const patchValue = patchSettingValue;
            const patchOperation = ConfigPatchOperationHelper.parseECSOperation(settingName, patchValue);
            ConfigPatchOperationHelper.applyECSPatchOperation(patchOperation, resultSettingValue);
            return resultSettingValue;
        }
        catch (error) {
            Log.error(0x1e25b7a3 /* tag_4j149 */, LogCategory.CoreDefault, `Exception thrown while applying ECS PATCH operation on setting. Error: ${error}`);
            return settingValue;
        }
    }
}
Setting.allSettings = new Map();
Setting.allPatternSettings = new Map();
Setting.ecsPatchConfigEnabled = false;
Setting.troubleshootUrl = 'https://eng.ms/docs/experiences-devices/opg/office-ai/augloop-ai-platform/augmentation-loop/documentation/server-workflow-tutorials/settings/troubleshoot';
/**
 * A type-safe instance of a given setting
 * with an optional default value
 */
export class SettingInstance extends DataListener {
    constructor(name, defaultValue) {
        super();
        this.listenerId = NaN;
        this.defaultValue = defaultValue;
        this.setting = Setting.getInstance(name);
    }
    /**
     * Returns default value for this instance
     */
    getDefaultValue() {
        return this.defaultValue;
    }
    addListener(callback) {
        if (Number.isNaN(this.listenerId)) {
            this.listenerId = this.setting.addListener(() => {
                this.notifyListeners(this.getValue());
            });
        }
        return super.addListener(callback);
    }
    removeListener(id) {
        super.removeListener(id);
        if (this.listeners.size == 0 && !Number.isNaN(this.listenerId)) {
            this.setting.removeListener(this.listenerId);
            this.listenerId = NaN;
        }
    }
    /**
     * Returns the current setting value
     */
    getValue() {
        const value = this.setting.getValue();
        return (value === undefined) ? this.defaultValue : value;
    }
    /**
    * Advanced method to get the current setting value with support for custom allocation % per user
    */
    getValueAdvancedAsync(input) {
        return __awaiter(this, void 0, void 0, function* () {
            const value = yield this.setting.getValueAdvancedAsync(input);
            return (value === undefined) ? this.defaultValue : value;
        });
    }
}
/**
 * A type-safe instance of an array of settings with names matching given pattern
 */
export class SettingPatternInstance extends DataListener {
    constructor(pattern) {
        super();
        this.listenerId = NaN;
        this.setting = Setting.getPatternInstance(pattern);
    }
    addListener(callback) {
        if (Number.isNaN(this.listenerId)) {
            this.listenerId = this.setting.addListener(() => {
                this.notifyListeners(this.getValue());
            });
        }
        return super.addListener(callback);
    }
    removeListener(id) {
        super.removeListener(id);
        if (this.listeners.size == 0 && !Number.isNaN(this.listenerId)) {
            this.setting.removeListener(this.listenerId);
            this.listenerId = NaN;
        }
    }
    /**
     * Returns the current setting value
     */
    getValue() {
        const value = this.setting.getValue();
        return (value === undefined) ? [] : value;
    }
}
//# sourceMappingURL=setting.js.map